import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Pencil, Trash2, User as UserIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { postAPI, userPostAPI } from '../utils/api';
import { getLocalized } from '../utils/postLocalization';
import PostImageGallery from '../components/PostImageGallery';
import CreatePostModal from '../components/CreatePostModal';
import ConfirmModal from '../components/ConfirmModal';

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isAdmin = location.pathname.startsWith('/posts');
  const api = isAdmin ? postAPI : userPostAPI;
  const listPath = isAdmin ? '/posts' : '/panel/posts';

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingConfirm, setDeletingConfirm] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getOne(id);
      setPost(res.data.data);
    } catch (error) {
      toast.error(t('installationInstructions.errors.fetchOne'));
      navigate(listPath);
    } finally {
      setLoading(false);
    }
  }, [id, api, navigate, listPath, t]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleUpdate = async (formData) => {
    setUpdating(true);
    try {
      await api.update(post._id, formData);
      toast.success(t('installationInstructions.success.update'));
      setEditModalOpen(false);
      await fetchDetail();
    } catch (error) {
      toast.error(error.response?.data?.message || t('installationInstructions.errors.update'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    setDeletingConfirm(true);
    try {
      await api.delete(post._id);
      toast.success(t('installationInstructions.success.delete'));
      navigate(listPath);
    } catch (error) {
      toast.error(error.response?.data?.message || t('installationInstructions.errors.delete'));
    } finally {
      setDeletingConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
      </div>
    );
  }

  if (!post) return null;

  const title = getLocalized(post.title, i18n.language);
  const body = getLocalized(post.body, i18n.language);
  const authorName = post.createdBy?.fullName || t('common.administrator');

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <button
        onClick={() => navigate(listPath)}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('installationInstructions.back')}
      </button>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {post.displayId && (
          <p className="text-xs font-mono text-gray-400 mb-2">#{post.displayId}</p>
        )}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{title}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <UserIcon className="w-4 h-4" />
            {t('installationInstructions.detail.author')}: {authorName}
          </span>
          <span>{formatDateTime(post.createdAt)}</span>
          {isAdmin && (
            <>
              <button
                onClick={() => setEditModalOpen(true)}
                className="ml-auto flex items-center gap-1.5 text-primary-600 hover:text-primary-700 font-medium"
              >
                <Pencil className="w-4 h-4" />
                {t('installationInstructions.editButton')}
              </button>
              <button
                onClick={() => setDeleting(true)}
                className="flex items-center gap-1.5 text-red-600 hover:text-red-700 font-medium"
              >
                <Trash2 className="w-4 h-4" />
                {t('installationInstructions.deleteButton')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">{body}</div>
        {post.images && post.images.length > 0 && (
          <div className="mt-6">
            <PostImageGallery images={post.images} />
          </div>
        )}
      </div>

      {isAdmin && (
        <>
          <CreatePostModal
            isOpen={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            onSubmit={handleUpdate}
            initialData={post}
            submitting={updating}
          />

          <ConfirmModal
            isOpen={deleting}
            onClose={() => setDeleting(false)}
            onConfirm={handleDelete}
            title={t('installationInstructions.confirm.title')}
            message={t('installationInstructions.confirm.message')}
            confirmText={t('installationInstructions.confirm.confirmText')}
            cancelText={t('installationInstructions.confirm.cancelText')}
            submitting={deletingConfirm}
          />
        </>
      )}
    </div>
  );
};

export default PostDetail;
